/**
 * `start_call` — a Stream "user custom event" the Universe CRM sends to a guide
 * when they click a client's phone number (`POST admin/stream/call`,
 * `StreamController::call`). It carries the params the `/call` screen takes;
 * the app shows [StartCallPrompt] ("Do you want to start to call?") and the
 * guide picks WhatsApp or Uniphone there, then the call opens the same way
 * the channel header does.
 *
 * The CRM pushes calls over the websocket instead of a deep link: the shipped
 * desktop shell only understands the conversation link, and the native app's
 * `/call` route has its own param names, so a new link shape would need every
 * installed build to learn it first. Custom events are not persisted — with no
 * connection the event is dropped, and the CRM opens the launcher instead.
 *
 * Payload: `{ type: 'start_call', call: { channel?, channel_id, name, phone, image, phone_id? } }`.
 * The params are nested under `call` because `channel`, `channel_id` and `cid`
 * are reserved keys of every Stream event and Stream strips them from a custom
 * event's top level before delivering it. `channel` is the type the CRM button
 * was labelled with; the prompt offers both regardless, so it is carried but
 * not acted on.
 */
import type { StreamChat } from 'stream-chat';
import { parsePhoneIdParam } from './callParams';
import type { CallChannel } from './types';

export const START_CALL_EVENT = 'start_call';

/**
 * Params for the `/call` screen, all strings: they travel as route params
 * in-app and through `window.desktop.openCall` (a `Record<string, string>`).
 */
export type StartCallParams = Record<string, string> & {
    /** Set by the prompt when the guide picks; from the event it is only a hint. */
    channel?: CallChannel;
    channel_id: string;
    name: string;
    phone: string;
    image: string;
    phone_id?: string;
};

/** Minimum gap between two calls started by events (mirrors the header's double-click guard). */
const START_CALL_DEBOUNCE_MS = 1500;

function asString(value: unknown): string {
    return typeof value === 'string' ? value : value == null ? '' : String(value);
}

/**
 * Validates a `start_call` event into `/call` params. Returns `null` — and the
 * event is ignored — unless its `call` object names a Stream channel id.
 * `channel` is kept only when it is a known call type; `phone_id` only when it
 * is a positive integer (see [parsePhoneIdParam]) — the prompt drops it again
 * if the guide picks WhatsApp, which ignores it (and a bad one is a 422).
 */
export function parseStartCallEvent(event: unknown): StartCallParams | null {
    if (!event || typeof event !== 'object') return null;
    const call = (event as Record<string, unknown>).call;
    if (!call || typeof call !== 'object') return null;
    const data = call as Record<string, unknown>;

    const channelId = asString(data.channel_id).trim();
    if (!channelId) return null;

    const params: StartCallParams = {
        channel_id: channelId,
        name: asString(data.name),
        phone: asString(data.phone),
        image: asString(data.image),
    };

    if (data.channel === 'whatsapp' || data.channel === 'pstn') {
        params.channel = data.channel;
    }

    const phoneId = parsePhoneIdParam(asString(data.phone_id));
    if (phoneId !== undefined) {
        params.phone_id = String(phoneId);
    }

    return params;
}

/**
 * Listens for `start_call` on the Stream client and hands valid ones to
 * `onStartCall`. Returns the unsubscribe function.
 */
export function subscribeStartCall(
    client: StreamChat,
    onStartCall: (params: StartCallParams) => void,
): () => void {
    let lastStartedAt = 0;

    const handler = (event: unknown) => {
        const params = parseStartCallEvent(event);
        if (!params) {
            console.warn('[calls] ignoring malformed start_call event', event);
            return;
        }
        const now = Date.now();
        if (now - lastStartedAt < START_CALL_DEBOUNCE_MS) return;
        lastStartedAt = now;

        console.log('[calls] start_call', { channel_id: params.channel_id });
        onStartCall(params);
    };

    client.on(START_CALL_EVENT as any, handler);
    return () => {
        client.off(START_CALL_EVENT as any, handler);
    };
}
